import React, { useState, useEffect } from 'react';

const MaintenancePage = () => {
  const [isDarkMode, setIsDarkMode] = useState(false);
  
  // Check user's preferred color scheme on load
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme === 'dark') {
      setIsDarkMode(true);
      document.documentElement.classList.add('dark');
    } else if (savedTheme === 'light') {
      setIsDarkMode(false);
      document.documentElement.classList.remove('dark');
    } else if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      setIsDarkMode(true);
      document.documentElement.classList.add('dark');
    }
  }, []);

  // Toggle between dark and light mode
  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
    if (!isDarkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  };

  return (
    <div className={`min-h-screen flex flex-col items-center justify-center ${isDarkMode ? 'bg-[#0b0e11] text-white' : 'bg-white text-black'} px-4 py-12`}>
      {/* Theme toggle button */}
      <button
        onClick={toggleDarkMode}
        className="absolute top-4 right-4 w-12 h-6 md:w-14 md:h-7 rounded-full flex items-center transition-colors duration-300 z-30"
        style={{
          backgroundColor: isDarkMode ? "#2a2e33" : "#f1f4f6",
          boxShadow: isDarkMode ? "0px 2px 8px rgba(0, 0, 0, 0.3)" : "0px 2px 8px rgba(0, 0, 0, 0.1)"
        }}
      >
        {/* Toggle switch circle */}
        <div
          className={`w-4 h-4 md:w-5 md:h-5 rounded-full transform duration-300 flex items-center justify-center ${
            isDarkMode 
              ? "translate-x-7 md:translate-x-8 bg-gray-700" 
              : "translate-x-1 bg-white"
          }`}
          style={{
            boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.2)"
          }}
        >
          <i
            className={`fas ${isDarkMode ? "fa-moon" : "fa-sun"} text-[10px] md:text-xs ${
              isDarkMode ? "text-white" : "text-yellow-500"
            }`}
          ></i>
        </div>
      </button>
      
      <div className="max-w-3xl mx-auto text-center relative">
        {/* Decorative elements */}
        <div className={`absolute -top-16 -left-16 w-32 h-32 border-t-2 border-l-2 ${isDarkMode ? 'border-gray-800' : 'border-gray-200'}`}></div>
        <div className={`absolute -bottom-16 -right-16 w-32 h-32 border-b-2 border-r-2 ${isDarkMode ? 'border-gray-800' : 'border-gray-200'}`}></div>
        
        {/* Logo */}
        <div className="mb-8 inline-block">
          <div className="w-20 h-20 md:w-24 md:h-24 rounded-full flex items-center justify-center shadow-lg mb-4 mx-auto overflow-hidden bg-white">
            <img 
              src="/logo.png" 
              alt="BlippAI" 
              className="w-full h-full object-contain p-2" 
            />
          </div>
          <div className="flex items-center justify-center">
            <img 
              src="/logo.png" 
              alt="BlippAI" 
              className="h-6 w-auto mr-2" 
            />
            <h2 className="text-xl md:text-2xl font-semibold">BlippAI</h2>
          </div>
        </div>
        
        {/* Alert Icon */}
        <div className={`inline-flex items-center justify-center w-16 h-16 ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'} rounded-full mb-6 mx-auto`}>
          <svg xmlns="http://www.w3.org/2000/svg" className={`h-8 w-8 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        </div>
        
        {/* Main header */}
        <h1 className="text-3xl md:text-4xl font-bold mb-4">
          <span className="inline-block transform hover:scale-105 transition-transform duration-300">
            🚀 Level Up Alert! 🚨
          </span>
        </h1>
        
        {/* Message */}
        <div className={`${isDarkMode ? 'bg-gray-900 border-gray-800' : 'bg-gray-50 border-gray-100'} p-6 md:p-8 rounded-xl shadow-sm border mb-8`}>
          <p className="text-xl md:text-2xl font-semibold mb-4">
            We're turbocharging BlippAI with a massive boost in computing power, speed, and accuracy!
          </p>
          <p className="text-base md:text-lg mb-6">
            Our team is on a mission to make BlippAI the ultimate AI sidekick—think Robin to your Batman, but way more tech-savvy 😎. Get ready for the most epic AI adventure ever! 🚀
          </p>
          <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} font-medium`}>
            — The BlippAI Team
          </div>
        </div>
        
        {/* Countdown or additional info */}
        <div className={`inline-flex items-center space-x-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>We'll be back shortly</span>
        </div>
      </div>
      
      {/* Footer */}
      <div className={`mt-auto pt-16 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} flex items-center justify-center`}>
        <p>© {new Date().getFullYear()}</p>
        <img 
          src="/logo.png" 
          alt="BlippAI" 
          className="h-4 w-auto mx-2" 
        />
        <p>BlippAI. All rights reserved.</p>
      </div>
    </div>
  );
};

export default MaintenancePage; 